/**
 * SURVEY beats (brand lift / brand tracker studies) as the client sees them.
 *
 * A survey beat has no platform, no creators and no deliverables: it is a
 * study with a fieldwork window, a respondent target and an audience. This
 * module turns the client-facing api-v2 beat into that view.
 *
 * Client-safe by construction: it reads an explicit allowlist of fields
 * (`SURVEY_CLIENT_FIELDS` + the target group keys below) and nothing else.
 * Internal survey economics (price per respondent, panel cost, margin, take
 * rate) live on back-office endpoints only and must never be read here — the
 * client app does not show cost metrics.
 */
import type { TFunction } from 'i18next';
import type { SurveyTargetGroup } from '@/api/beatlyApi';

/** The only beat fields a client survey view may read. */
export const SURVEY_CLIENT_FIELDS = [
  'beat_id',
  'beat_name',
  'beat_type',
  'type',
  'status',
  'date_start',
  'date_end',
  'date_pretest_completed',
  'date_control_completed',
  'date_completed',
  'target_group',
] as const;

/** The only target-group fields a client survey view may read. */
export const SURVEY_CLIENT_TARGET_FIELDS = [
  'participants_total',
  'country',
  'language',
  'age_min',
  'age_max',
  'gender',
] as const;

export interface SurveyBeatInput {
  beat_id?: string;
  beat_name?: string | null;
  beat_type?: string | null;
  type?: string | null;
  status?: string | null;
  date_start?: string | null;
  date_end?: string | null;
  date_pretest_completed?: string | null;
  date_control_completed?: string | null;
  date_completed?: string | null;
  target_group?: SurveyTargetGroup | null;
}

export type SurveyPhase = 'planned' | 'pretest' | 'control' | 'fieldwork' | 'completed' | 'canceled';
export type SurveyGender = 'women' | 'men' | 'all';

export interface SurveyStep {
  key: 'start' | 'pretest' | 'control' | 'completed';
  /** ISO date when the step happened (or is planned, for `start`). */
  date?: string;
  done: boolean;
}

export interface SurveyBeatSummary {
  /** Beat name when set, else null (caller shows the generic "Survey" label). */
  name: string | null;
  phase: SurveyPhase;
  /** Fieldwork start (`date_start`). */
  fieldworkStart?: string;
  /** Completion date when completed, else the expected end (`date_completed`, then `date_end`). */
  resultsDate?: string;
  resultsDone: boolean;
  /** Respondents target (`target_group.participants_total`). */
  respondents?: number;
  country?: string;
  language?: string;
  ageMin?: number;
  ageMax?: number;
  gender?: SurveyGender;
  steps: SurveyStep[];
}

export function isSurveyBeatType(type: string | null | undefined): boolean {
  return (type ?? '').trim().toUpperCase() === 'SURVEY';
}

/** Beat type from either `beat_type` (beat detail) or `type` (campaign beats). */
export function surveyInputType(b: Pick<SurveyBeatInput, 'beat_type' | 'type'>): string | undefined {
  const t = (b.beat_type ?? '').trim() || (b.type ?? '').trim();
  return t || undefined;
}

export function isSurveyBeat(b: Pick<SurveyBeatInput, 'beat_type' | 'type'>): boolean {
  return isSurveyBeatType(surveyInputType(b));
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
}

function num(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() && Number.isFinite(Number(v))) return Number(v);
  return undefined;
}

function isPast(iso: string | undefined, now: Date): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) && t <= now.getTime();
}

function genderOf(raw: unknown): SurveyGender | undefined {
  const g = str(raw)?.toUpperCase();
  if (g === 'F') return 'women';
  if (g === 'M') return 'men';
  if (g === 'MF' || g === 'FM' || g === 'ALL') return 'all';
  return undefined;
}

/**
 * Builds the client view of a survey beat. Reads only the allowlisted fields;
 * anything else on the beat (including internal money fields) is ignored.
 */
export function summarizeSurveyBeat(beat: SurveyBeatInput, now: Date = new Date()): SurveyBeatSummary {
  const tg = (beat.target_group && typeof beat.target_group === 'object' ? beat.target_group : {}) as SurveyTargetGroup;
  const fieldworkStart = str(beat.date_start);
  const pretest = str(beat.date_pretest_completed);
  const control = str(beat.date_control_completed);
  const completed = str(beat.date_completed);
  const resultsDone = isPast(completed, now);
  const status = (beat.status ?? '').trim().toUpperCase();

  let phase: SurveyPhase;
  if (status === 'CANCELED') phase = 'canceled';
  else if (resultsDone || status === 'DONE') phase = 'completed';
  else if (isPast(control, now)) phase = 'fieldwork';
  else if (isPast(pretest, now)) phase = 'control';
  else if (isPast(fieldworkStart, now) || status === 'ONGOING') phase = 'pretest';
  else phase = 'planned';

  const ageMin = num(tg.age_min);
  const ageMax = num(tg.age_max);

  return {
    name: str(beat.beat_name) ?? null,
    phase,
    fieldworkStart,
    resultsDate: completed ?? str(beat.date_end),
    resultsDone: resultsDone || (phase === 'completed' && Boolean(completed)),
    respondents: num(tg.participants_total),
    country: str(tg.country),
    language: str(tg.language),
    ageMin,
    ageMax,
    gender: genderOf(tg.gender),
    steps: [
      { key: 'start', date: fieldworkStart, done: isPast(fieldworkStart, now) || phase !== 'planned' },
      { key: 'pretest', date: pretest, done: isPast(pretest, now) },
      { key: 'control', date: control, done: isPast(control, now) },
      { key: 'completed', date: completed, done: phase === 'completed' },
    ],
  };
}

/** "18–34", "18+", "up to 34" style age range, or undefined when unknown. */
export function formatSurveyAgeRange(min?: number, max?: number): string | undefined {
  if (min != null && max != null) return `${min}–${max}`;
  if (min != null) return `${min}+`;
  if (max != null) return `≤${max}`;
  return undefined;
}

function displayName(code: string, type: 'region' | 'language', locale: string): string {
  // Codes like "SE" / "sv"; anything longer is already a readable name.
  if (!/^[A-Za-z]{2,3}(-[A-Za-z]{2})?$/.test(code)) return code;
  try {
    const names = new Intl.DisplayNames([locale], { type });
    return names.of(type === 'region' ? code.toUpperCase() : code.toLowerCase()) ?? code;
  } catch {
    return code;
  }
}

/** Country code → readable name ("SE" → "Sweden"); passes through names. */
export function surveyCountryName(code: string | undefined, locale = 'en'): string | undefined {
  return code ? displayName(code, 'region', locale) : undefined;
}

/** Language code → readable name ("sv" → "Swedish"); passes through names. */
export function surveyLanguageName(code: string | undefined, locale = 'en'): string | undefined {
  return code ? displayName(code, 'language', locale) : undefined;
}

/** Same short date the beats list uses ("3 Nov 2026"); '' when invalid. */
export function formatSurveyDate(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Display label: the beat name when set, else the generic "Survey". */
export function surveyBeatLabel(summary: Pick<SurveyBeatSummary, 'name'>, t: TFunction): string {
  return summary.name ?? t('campaigns.survey.title');
}

/** One-line summary for a beat row: "Fieldwork from 3 Nov 2026 · Results 24 Nov 2026 · 1,000 respondents". */
export function surveyBeatRowText(summary: SurveyBeatSummary, t: TFunction, locale = 'en'): string {
  const parts: string[] = [];
  const start = formatSurveyDate(summary.fieldworkStart);
  const results = formatSurveyDate(summary.resultsDate);
  if (start) parts.push(t('campaigns.survey.row.fieldwork', { date: start }));
  if (results) {
    parts.push(t(summary.resultsDone ? 'campaigns.survey.row.completed' : 'campaigns.survey.row.results', { date: results }));
  }
  if (summary.respondents != null) {
    parts.push(t('campaigns.survey.row.respondents', { count: summary.respondents, formatted: summary.respondents.toLocaleString(locale) }));
  }
  return parts.length ? parts.join(' · ') : '–';
}
